import {
    type ReactElement,
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react'

import WebApp from '@twa-dev/sdk'

import { useTokenWorker } from '@/hooks/token'

import { v1Options, v1Exercise } from '@/lib/api/pocket'
import appState from '@/lib/domain/appstate'
import i18n from '@/lib/domain/i18n'
import { getUserLanguage } from '@/lib/languages'

import {
    exercisesMap,
    fetchAppContext,
    fetchExercise,
    fetchI18n
} from './staticdata'

import { useThemeMode } from '@/hooks/theme'

interface AppContextInterface {
    i18n: i18n | undefined
    appState: appState
    loading: boolean
    webapp?: typeof WebApp
    theme: 'light' | 'dark'
    exerciseList: v1Exercise[]
    geti18n: (slug: string) => string
    getOptions: (slug: string) => v1Options | undefined
    getOptionKeyByValue: (slug: string, value: string) => string | undefined
    getExercise: (slug: string) => v1Exercise | undefined
    /** Required static config (app context or i18n) could not be loaded. */
    staticError: boolean
    retryStatic: () => void
}

const initialContext: AppContextInterface = {
    i18n: {},
    appState: {
        navigation: [],
        forms: {},
        options: {}
    },
    exerciseList: [],
    loading: true,
    theme: 'light',
    geti18n: (_slug: string) => _slug,
    getOptions: (_slug: string) => undefined,
    getOptionKeyByValue: (_slug: string, _value: string) => undefined,
    getExercise: (_slug: string) => undefined,
    staticError: false,
    retryStatic: () => {}
}

export const AppContext = createContext<AppContextInterface>(initialContext)
type AppContextProps = { children: ReactElement }

export default function AppContextProvider({
    children
}: AppContextProps): ReactElement<AppContextInterface> {
    const [i18nData, seti18nData] = useState<i18n | undefined>(undefined)
    const [appStateData, setAppStateData] = useState<appState>(
        initialContext.appState
    )
    const [exercisesData, setExercisesData] = useState<v1Exercise[]>([])
    const [exercises, setExercises] = useState<exercisesMap>({})
    const { mode } = useThemeMode()
    const [loading, setLoading] = useState(true)
    const [staticError, setStaticError] = useState(false)

    const fetchAllAsync = useCallback(async () => {
        setStaticError(false)
        setLoading(true)

        const [i18nDataRaw, appStateDataRaw, exerciseDataRaw] =
            await Promise.all([fetchI18n(), fetchAppContext(), fetchExercise()])

        // Navigation, forms and copy are required config: without them the
        // app renders raw slugs and empty menus, which reads as a broken
        // build rather than an outage. Exercises stay optional.
        const requiredConfigMissing =
            appStateDataRaw === undefined ||
            Object.keys(i18nDataRaw).length === 0

        seti18nData(i18nDataRaw)
        setAppStateData(appStateDataRaw ?? initialContext.appState)
        setExercisesData(exerciseDataRaw)
        setExercises(
            exerciseDataRaw.reduce<exercisesMap>((map, obj) => {
                map[obj.slug] = obj
                map[obj.id] = obj
                return map
            }, {})
        )

        setStaticError(requiredConfigMissing)
        setLoading(false)
    }, [])

    const retryStatic = useCallback(() => {
        void fetchAllAsync()
    }, [fetchAllAsync])

    const value = useMemo(
        () => ({
            i18n: i18nData,
            theme: mode,
            appState: appStateData,
            exerciseList: exercisesData,
            loading,
            webapp: WebApp,
            geti18n: (slug: string) => {
                if (!i18nData) return '...'
                return (
                    i18nData[getUserLanguage() + ':' + slug] ||
                    i18nData[slug] ||
                    slug
                )
            },
            getOptions: (slug: string) => {
                if (!appStateData.options) return undefined
                return appStateData.options[slug] || undefined
            },
            getOptionKeyByValue: (slug: string, value: string) => {
                if (!appStateData.options) return undefined
                return appStateData.options[slug]?.valueToKey[value]
            },
            getExercise: (slug: string) => {
                return exercises[slug]
            },
            staticError,
            retryStatic
        }),
        [
            i18nData,
            mode,
            appStateData,
            exercisesData,
            loading,
            exercises,
            staticError,
            retryStatic
        ]
    )

    useEffect(() => {
        fetchAllAsync()
    }, [fetchAllAsync])
    useTokenWorker()

    useEffect(() => {
        const color = mode === 'light' ? '#fbfcfe' : '#0c0d0e'
        WebApp.setHeaderColor(color)
        WebApp.setBottomBarColor(color)
        WebApp.setBackgroundColor(color)
    }, [mode])

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}
